import { recruitmentRoundLabel } from "../../services/recruitmentLabels";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  MonitoringData,
  recruitmentAPI,
  recruitmentError,
} from "../../services/recruitmentAPI";
export default function MonitoringRecordsPage() {
  const [data, setData] = useState<MonitoringData>();
  const [drive, setDrive] = useState("");
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hidden, setHidden] = useState<string[]>([]);
  const [order, setOrder] = useState<string[]>([]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    recruitmentAPI
      .monitoring({
        drive_id: drive ? Number(drive) : undefined,
        college_type: type || undefined,
        page,
      })
      .then(({ data }) => {
        if (active) setData(data);
      })
      .catch((e) => {
        if (active) setError(recruitmentError(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [drive, type, page, refresh]);
  const columns = [
    { id: "college", name: "Name of College" },
    { id: "drive", name: "Drive" },
    { id: "applied", name: "Students Applied" },
    { id: "shortlisted", name: "Shortlisted for Assessment" },
    { id: "assessment_passed", name: "Assessment Passed" },
    ...(data?.columns || []).map((r) => ({
      id: `round-${r.round_id}`,
      name: recruitmentRoundLabel(r.name),
    })),
  ];
  const sorted = [...columns].sort((a, b) => {
    const rank = (id: string) =>
      order.includes(id)
        ? order.indexOf(id)
        : order.length + columns.findIndex((c) => c.id === id);
    return rank(a.id) - rank(b.id);
  });
  const visible = sorted.filter((c) => !hidden.includes(c.id));
  const rows = (data?.rows || []).filter((r) =>
    `${r.college} ${r.drive}`.toLowerCase().includes(search.toLowerCase()),
  );
  const cell = (
    row: NonNullable<typeof data>["rows"][number],
    id: string,
  ): string | number =>
    id.startsWith("round-")
      ? (row.rounds[id.slice(6)] ?? "—")
      : row[
          id as
            | "college"
            | "drive"
            | "applied"
            | "shortlisted"
            | "assessment_passed"
        ];
  const move = (id: string, direction: number) => {
    const ids = sorted.map((c) => c.id);
    const i = ids.indexOf(id),
      n = i + direction;
    if (n >= 0 && n < ids.length) {
      [ids[i], ids[n]] = [ids[n], ids[i]];
      setOrder(ids);
    }
  };
  const download = () => {
    const book = XLSX.utils.book_new();
    const sheet = XLSX.utils.aoa_to_sheet([
      visible.map((c) => c.name),
      ...rows.map((r) => visible.map((c) => cell(r, c.id))),
    ]);
    sheet["!cols"] = visible.map(() => ({ wch: 24 }));
    XLSX.utils.book_append_sheet(book, sheet, "Monitoring Records");
    XLSX.writeFile(book, "monitoring-records.xlsx");
  };
  return (
    <main className="mx-auto max-w-screen-2xl p-6 space-y-5">
      <Link to="/dashboard" className="text-blue-600">
        ← Back to Dashboard
      </Link>
      <div className="flex flex-wrap justify-between gap-3">
        <h1 className="text-2xl font-bold">Monitoring Records</h1>
        <div className="flex gap-3">
          <button
            disabled={loading}
            onClick={() => setRefresh((v) => v + 1)}
            className="rounded border px-4 py-2"
          >
            Refresh
          </button>
          <button
            disabled={loading || !data || !visible.length}
            onClick={download}
            className="rounded bg-green-600 px-4 py-2 text-white disabled:opacity-50"
          >
            Export Excel (current page)
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 rounded-xl border bg-white p-4">
        <label>
          College Type
          <select
            className="ml-2 rounded border p-2"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setDrive("");
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="management">Management</option>
            <option value="engineering">Engineering</option>
          </select>
        </label>
        <label>
          Drive
          <select
            className="ml-2 rounded border p-2 max-w-xs"
            value={drive}
            onChange={(e) => {
              setDrive(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All drives</option>
            {data?.drives.map((d) => (
              <option key={d.id} value={d.id}>
                {d.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          Search this page
          <input
            className="ml-2 rounded border p-2"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="College or drive"
          />
        </label>
      </div>
      <details className="rounded border bg-white p-4">
        <summary className="cursor-pointer font-medium">
          Customize Columns
        </summary>
        <div className="mt-3 flex flex-wrap gap-3">
          {sorted.map((c, i) => (
            <div
              key={c.id}
              className="flex items-center gap-2 rounded border p-2"
            >
              <label>
                <input
                  type="checkbox"
                  checked={!hidden.includes(c.id)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setHidden((previous) =>
                      checked
                        ? previous.filter((id) => id !== c.id)
                        : [...previous, c.id],
                    );
                  }}
                />{" "}
                {c.name}
              </label>
              <button
                aria-label={`Move ${c.name} left`}
                disabled={i === 0}
                onClick={() => move(c.id, -1)}
              >
                ←
              </button>
              <button
                aria-label={`Move ${c.name} right`}
                disabled={i === sorted.length - 1}
                onClick={() => move(c.id, 1)}
              >
                →
              </button>
            </div>
          ))}
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Management recruitment rounds are fixed. Changes to customizable drive rounds appear here automatically.
        </p>
      </details>
      <p className="text-sm text-gray-600">
        TPOs forward the assessment link to students. Registered students are
        shortlisted for assessment automatically. Counts reflect saved recruiter
        decisions.
      </p>
      {error && (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      )}
      {loading ? (
        <p role="status">Loading monitoring records...</p>
      ) : (
        !error && (
          <div className="overflow-x-auto rounded border bg-white">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-green-100">
                <tr>
                  {visible.map((c) => (
                    <th
                      key={c.id}
                      className="whitespace-nowrap border border-gray-300 p-3 text-left"
                    >
                      {c.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.drive_id}-${i}`} className="even:bg-gray-50">
                    {visible.map((c) => (
                      <td
                        key={c.id}
                        className="whitespace-nowrap border border-gray-300 p-3"
                      >
                        {c.id === "drive" ? (
                          <Link
                            className="text-blue-600"
                            to={`/recruiter/drives/${r.drive_id}`}
                          >
                            {r.drive}
                          </Link>
                        ) : (
                          cell(r, c.id)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length && (
              <p className="p-5 text-gray-500">
                No records match these filters.
              </p>
            )}
          </div>
        )
      )}
      <div className="flex items-center gap-4">
        <button
          className="rounded border px-3 py-2"
          disabled={page <= 1 || loading}
          onClick={() => setPage((v) => v - 1)}
        >
          Previous
        </button>
        <span>
          Page {page} · {data?.total_drives || 0} drives
        </span>
        <button
          className="rounded border px-3 py-2"
          disabled={
            loading || !data || page * data.page_size >= data.total_drives
          }
          onClick={() => setPage((v) => v + 1)}
        >
          Next
        </button>
      </div>
    </main>
  );
}
